import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import type {
  Booking,
  BookingAgent,
  BookingLineDetail,
  BookingProfit,
  CostRow,
  MoneyRow,
  Page,
} from "@/lib/types";
import { useAuth } from "@/auth/auth";
import { BookingStatusBadge, Button, Card, ErrorNote, Spinner, StatusBadge } from "@/components/ui";
import { formatMinor } from "@/lib/hooks";
import { ConfirmButton } from "@/components/ConfirmButton";
import { RowActions } from "@/components/RowActions";
import { CostLineEditor } from "@/components/CostLineEditor";
import { DateField, Labeled, Select, TextArea, TextField } from "@/components/Form";
import { CustomerPicker } from "@/components/CustomerPicker";
import { BookingAgentFields } from "@/components/BookingAgentFields";
import {
  agentDraftChanged,
  agentDraftFromBooking,
  agentDraftIsValid,
  agentDraftToPayload,
  type AgentDraft,
} from "@/lib/bookingAgent";
import { BookingOperations } from "@/components/TaskManager";
import { BookingMoneyCollector } from "@/components/BookingMoneyCollector";
import { BookingLinesSection, lineLabel } from "@/components/BookingLines";
import {
  FinanceLineForm,
  type CostLineOption,
  type FinanceLineKind,
} from "@/components/FinanceLineForm";
import { dateRange } from "@/pages/BookingsListPage";
import { formatCompanyDate, formatCompanyDateTime, formatDateOnly } from "@/lib/time";

const CURRENCIES = ["AED", "USD", "EUR", "RUB", "SAR"];

function Field({
  label,
  value,
  amount = false,
}: {
  label: string;
  value: React.ReactNode;
  /** a money figure of the finance block: 20px bold */
  amount?: boolean;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={"mt-0.5" + (amount ? " nm-amount" : "")}>{value ?? "—"}</dd>
    </div>
  );
}

export default function BookingDetailPage() {
  const { t } = useTranslation();
  const { ref = "" } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canWrite = can("bookings:write");
  const [editing, setEditing] = useState(false);
  const remove = useEntityAction([["bookings"], ["booking", ref]], {
    inlineError: true,
    successMessage: t("common.deleted"),
    onSuccess: () => navigate("/bookings"),
  });
  const save = useEntityAction([["bookings"], ["booking", ref]], {
    inlineError: true,
    onSuccess: () => setEditing(false),
  });

  const bookingQuery = useQuery({
    queryKey: ["booking", ref],
    queryFn: () => api.get<Booking>(`/bookings/${ref}`),
  });

  const profitQuery = useQuery({
    queryKey: ["booking", ref, "profit"],
    queryFn: () => api.get<BookingProfit>(`/bookings/${ref}/profit`),
    enabled: can("finance:read"),
    retry: false,
  });

  if (bookingQuery.isLoading) return <Spinner />;
  if (bookingQuery.isError) {
    const err = bookingQuery.error;
    const msg =
      err instanceof ApiError && err.status === 404
        ? t("bookings.notFound")
        : (err as Error).message;
    return <ErrorNote message={msg} />;
  }

  const b = bookingQuery.data!;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/bookings" className="text-sm text-sand-muted hover:text-gold">
          {t("bookings.back")}
        </Link>
        <h1 className="text-lg font-semibold">{b.booking_number}</h1>
        <BookingStatusBadge booking={b} />
        {canWrite && !editing && (
          <div className="ml-auto flex items-center gap-2">
            <Button
              className="px-3 py-1 text-xs"
              onClick={() => setEditing(true)}
            >
              {t("common.edit")}
            </Button>
            <ConfirmButton
              label={t("common.delete")}
              busy={remove.isPending}
              onConfirm={() => remove.mutate(() => api.del(`/bookings/${ref}`))}
            />
          </div>
        )}
      </div>
      {remove.isError && (
        <ErrorNote message={(remove.error as Error).message} />
      )}

      {editing ? (
        <BookingEditForm
          booking={b}
          busy={save.isPending}
          error={save.isError ? (save.error as Error).message : null}
          onCancel={() => setEditing(false)}
          onSave={(patch) =>
            save.mutate(() => api.patch(`/bookings/${ref}`, patch))
          }
        />
      ) : (
        <Card>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field
              label={t("fields.customer")}
              value={
                b.customer_name ?? (
                  <span className="rounded-md border border-amber-400 px-2 py-0.5 text-amber-700">
                    {t("bookings.noCustomer")}
                    {canWrite && (
                      <button
                        className="ml-2 underline hover:text-gold"
                        onClick={() => setEditing(true)}
                      >
                        {t("bookings.setCustomer")}
                      </button>
                    )}
                  </span>
                )
              }
            />
            <Field
              label={t("fields.customerRecord")}
              value={
                b.customer_id ? (
                  <Link
                    className="text-sand hover:text-gold"
                    to={`/customers/${b.customer_id}`}
                  >
                    {t("common.open")}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Field label={t("fields.travelDates")} value={dateRange(b)} />
            <Field label={t("fields.currency")} value={b.currency} />
            <Field
              label={t("bookingAgent.origin")}
              value={
                b.agent ? (
                  <span>
                    {t("bookingAgent.fromAgent")}:{" "}
                    <Link className="text-sand hover:text-gold" to="/agents">
                      {b.agent.agent_name ?? "—"}
                    </Link>
                    {b.agent.agent_phone && (
                      <span className="text-sand-muted"> · {b.agent.agent_phone}</span>
                    )}
                  </span>
                ) : (
                  t("bookingAgent.direct")
                )
              }
            />
            {b.agent && (
              <Field
                label={t("bookingAgent.commission")}
                value={<CommissionTerms agent={b.agent} />}
              />
            )}
            {b.agent && b.agent.state && b.agent.state !== "void" && (
              <Field
                label={t("bookingAgent.payout")}
                value={<AgentPayoutText agent={b.agent} />}
              />
            )}
            <Field label={t("fields.linkedLead")} value={b.lead_id ?? "—"} />
            <Field label={t("fields.created")} value={formatCompanyDateTime(b.created_at)} />
            <Field label={t("fields.updated")} value={formatCompanyDateTime(b.updated_at)} />
          </dl>
          {b.notes && (
            <div className="mt-4">
              <Field label={t("fields.notes")} value={b.notes} />
            </div>
          )}
        </Card>
      )}

      {can("finance:read") && (
        <BookingMoneyCard
          profit={profitQuery.data}
          loading={profitQuery.isLoading}
          error={profitQuery.isError ? (profitQuery.error as Error) : null}
          currency={b.currency}
          booking={b}
          canWrite={can("finance:write") && can("bookings:write")}
        />
      )}

      {can("finance:read") && b.status !== "draft" && (
        <BookingMoneyCollector
          booking={b}
          balanceMinor={profitQuery.data?.balance_minor}
          canWrite={can("finance:write") && can("bookings:write")}
        />
      )}

      {can("bookings:read") && (
        <BookingLinesSection
          bookingRef={b.booking_number}
          currency={b.currency}
          canWrite={can("bookings:write")}
          canWriteCosts={can("finance:write")}
          canAssign={can("operations:assign")}
          canComplete={can("bookings:write") && can("operations:write")}
          bookingStatus={b.status}
          customerName={b.customer_name}
        />
      )}

      {can("operations:read") && (
        <BookingOperations
          bookingId={b.id}
          bookingRef={b.booking_number}
          canWrite={can("operations:write")}
          canAssign={can("operations:assign")}
        />
      )}

      {can("finance:read") && (
        <BookingFinanceLines
          bookingRef={ref}
          bookingId={b.id}
          currency={b.currency}
          canWrite={can("finance:write")}
        />
      )}
    </div>
  );
}

/** «7,5 % от продажи» / «фиксированная 150.00 AED». */
function CommissionTerms({ agent }: { agent: BookingAgent }) {
  const { t } = useTranslation();
  if (agent.commission_type === "none") return <>{t("bookingAgent.none")}</>;
  return agent.commission_type === "percent" ? (
    <>{t("bookingAgent.percentOfSale", { percent: agent.commission_percent })}</>
  ) : (
    <>
      {t("bookingAgent.fixedShort")}{" "}
      {formatMinor(agent.commission_amount_minor ?? 0, agent.currency)}
    </>
  );
}

/** What we owe / have paid the agent on this booking (owner rules 9-10): payable
 * only once its lines are completed, «Ожидается» until then, paid ones apart. */
function AgentPayoutText({ agent }: { agent: BookingAgent }) {
  const { t } = useTranslation();
  const cur = (minor: number | null) => formatMinor(minor ?? 0, agent.currency);
  switch (agent.state) {
    case "due":
      return (
        <strong className="text-gold">
          {t("bookingAgent.due")}: {cur(agent.owed_minor)}
        </strong>
      );
    case "expected":
      return (
        <>
          {t("finance.expected")}: {cur(agent.expected_minor)}
        </>
      );
    case "partial":
      return (
        <>
          {t("bookingAgent.paidAmount", { amount: cur(agent.paid_minor) })} ·{" "}
          {t("finance.expected").toLowerCase()} {cur(agent.expected_minor)}
        </>
      );
    case "paid":
      return <>{t("bookingAgent.paidAmount", { amount: cur(agent.paid_minor) })}</>;
    default:
      return <>—</>;
  }
}

/** The booking's one money block: sale, costs, profit, paid, balance — and,
 * apart, what is *earned* (completed lines only) vs *expected* (the rest).
 * All of it comes from `GET /bookings/{ref}/profit`, which derives everything
 * from the booking's lines (`PostgresBookingRepository.compute_profit`). */
function BookingMoneyCard({
  profit,
  loading,
  error,
  currency,
  booking,
  canWrite,
}: {
  profit: BookingProfit | undefined;
  loading: boolean;
  error: Error | null;
  currency: string;
  booking: Booking;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const ref = booking.booking_number;
  const received = useEntityAction([
    ["booking", ref],
    ["bookings"],
    ["booking-lines", ref],
    ["finance-lines", ref],
  ]);
  if (loading) return <Spinner />;
  if (error) return <ErrorNote message={error.message} />;
  if (!profit) return null;
  const cur = (minor: number) => formatMinor(minor, currency);
  const hasSplit =
    profit.earned_revenue_minor !== 0 ||
    profit.expected_revenue_minor !== 0 ||
    profit.earned_profit_minor !== 0 ||
    profit.expected_profit_minor !== 0;
  return (
    <Card>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Field
          amount
          label={profit.from_lines ? t("bookings.sale") : t("bookings.revenue")}
          value={cur(profit.revenue_minor)}
        />
        <Field amount label={t("bookings.supplierCost")} value={cur(profit.supplier_cost_minor)} />
        {profit.commission_minor !== 0 && (
          <Field amount label={t("bookings.commission")} value={cur(profit.commission_minor)} />
        )}
        <Field
          amount
          label={t("bookings.grossProfit")}
          value={<strong>{cur(profit.gross_profit_minor)}</strong>}
        />
        {profit.from_lines && (
          <>
            <Field amount label={t("bookings.paid")} value={cur(profit.paid_minor)} />
            <Field
          amount
              label={t("bookings.balance")}
              value={<strong>{cur(profit.balance_minor)}</strong>}
            />
          </>
        )}
        {profit.with_driver_minor > 0 && (
          <Field amount label={t("bookings.withDriver")} value={cur(profit.with_driver_minor)} />
        )}
      </dl>
      {profit.with_driver_minor > 0 && (
        <p className="mt-3 text-xs text-gold">{t("bookings.withDriverHint")}</p>
      )}
      {profit.overpaid_minor > 0 && (
        <p role="alert" className="mt-3 text-sm font-semibold text-red-700">
          {t("bookings.overpaid", { amount: cur(profit.overpaid_minor) })}
        </p>
      )}
      {canWrite && profit.from_lines && !["draft", "cancelled", "refunded"].includes(booking.status) && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {(profit.balance_minor > 0 ||
            ((booking.money_collected_by === "agent" || booking.money_collected_by === "driver") &&
              !booking.money_handed_over_at)) && (
            <ConfirmButton
              danger={false}
              label={t("bookings.moneyReceived", { amount: cur(Math.max(0, profit.balance_minor)) })}
              confirmLabel={t("bookings.moneyReceivedConfirm", {
                amount: cur(Math.max(0, profit.balance_minor)),
              })}
              busy={received.isPending}
              onConfirm={() => received.mutate(() => api.post(`/bookings/${ref}/money-received`))}
            />
          )}
          {profit.money_received_at && (
            <>
              <span className="text-xs text-emerald-700">
                {t("bookings.moneyReceivedAt", {
                  when: formatCompanyDateTime(profit.money_received_at),
                })}
              </span>
              <ConfirmButton
                label={t("bookings.moneyReceivedUndo")}
                busy={received.isPending}
                onConfirm={() =>
                  received.mutate(() => api.post(`/bookings/${ref}/money-received/undo`))
                }
              />
            </>
          )}
          <span className="text-xs text-sand-muted">{t("bookings.moneyReceivedHint")}</span>
        </div>
      )}
      {hasSplit && (
        <table className="nm-table mt-4 w-full border-t border-olive-600 pt-3 text-sm">
          <thead>
            <tr className="text-left text-xs text-sand-muted">
              <th className="pt-3 font-normal" />
              <th className="pt-3 text-right font-normal">{t("bookings.revenue")}</th>
              <th className="pt-3 text-right font-normal">{t("bookings.grossProfit")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-1">
                {t("finance.earned")}
                <span className="ml-2 text-xs text-sand-muted">{t("finance.earnedHint")}</span>
              </td>
              <td className="py-1 text-right">{cur(profit.earned_revenue_minor)}</td>
              <td className="py-1 text-right font-medium">{cur(profit.earned_profit_minor)}</td>
            </tr>
            <tr>
              <td className="py-1">
                {t("finance.expected")}
                <span className="ml-2 text-xs text-sand-muted">{t("finance.expectedHint")}</span>
              </td>
              <td className="py-1 text-right">{cur(profit.expected_revenue_minor)}</td>
              <td className="py-1 text-right">{cur(profit.expected_profit_minor)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}

const LINE_KINDS = [
  { kind: "payment", path: "payments", addKey: "addPayment", titleKey: "payments" },
  { kind: "cost", path: "costs", addKey: "addCost", titleKey: "costs" },
] as const;

const LINE_PATH: Record<FinanceLineKind, string> = {
  payment: "payments",
  cost: "costs",
};

function BookingFinanceLines({
  bookingRef,
  bookingId,
  currency,
  canWrite,
}: {
  bookingRef: string;
  bookingId: string;
  currency: string;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<FinanceLineKind | null>(null);
  const [showVoided, setShowVoided] = useState(false);
  const add = useEntityAction(
    [["booking", bookingRef], ["finance-lines", bookingRef], ["booking-lines", bookingRef]],
    { inlineError: true, onSuccess: () => setOpen(null) },
  );
  // The booking's lines, in page order: an extra cost is attached to one of them (owner
  // rule 19) — by default the first with a car / driver — and each cost row names the
  // line it sits on and can be moved with «Изменить».
  const linesQuery = useQuery({
    queryKey: ["booking-lines", bookingRef],
    queryFn: () => api.get<BookingLineDetail[]>(`/bookings/${bookingRef}/lines`),
  });
  const costLines: CostLineOption[] = (linesQuery.data ?? []).map((l) => ({
    id: l.id,
    label: `${lineLabel(l)} · ${formatDateOnly(l.service_date)}`,
  }));

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <h2 className="text-sm font-semibold text-sand">
          {t("bookings.financeLines")}
        </h2>

        {canWrite && (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-sand-muted">
            <input
              type="checkbox"
              checked={showVoided}
              onChange={(e) => setShowVoided(e.target.checked)}
            />
            {t("finance.showVoided")}
          </label>
        )}
      </div>
      <p className="mb-3 text-xs text-sand-muted">
        {t("bookings.financeLinesHint")}
      </p>
      <div className="space-y-3">
        {LINE_KINDS.map(({ kind, path, addKey, titleKey }) => (
          <Card key={kind}>
            <div className="flex items-center gap-3">
              <h3 className="text-xs font-semibold text-sand-muted">
                {t(`bookings.${titleKey}` as const)}
              </h3>
              {canWrite && open !== kind && (
                <Button
                  className="ml-auto px-3 py-1 text-xs"
                  onClick={() => {
                    add.reset();
                    setOpen(kind);
                  }}
                >
                  {t(`bookings.${addKey}` as const)}
                </Button>
              )}
            </div>

            <FinanceLineList
              bookingRef={bookingRef}
              bookingId={bookingId}
              kind={kind}
              canWrite={canWrite}
              showVoided={showVoided}
              costLines={costLines}
            />

            {canWrite && open === kind && (
              <div className="mt-3">
                <FinanceLineForm
                  kind={kind}
                  defaultCurrency={currency}
                  lines={kind === "cost" ? costLines : []}
                  busy={add.isPending}
                  error={add.isError ? (add.error as Error).message : null}
                  onCancel={() => setOpen(null)}
                  onSubmit={(payload) =>
                    add.mutate(() =>
                      api.post(`/bookings/${bookingRef}/${path}`, payload),
                    )
                  }
                />
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

/** «→ Экскурсия · 18.09» — the line a cost sits on now, with «авто» when the owner did
 * not pick it (so it is clear the system chose, and can move it). */
function CostLineTag({ row, costLines }: { row: CostRow; costLines: CostLineOption[] }) {
  const { t } = useTranslation();
  const label = costLines.find((l) => l.id === row.effective_line_id)?.label;
  if (!label) return null;
  return (
    <span className="text-xs text-sand-muted">
      → {label}
      {!row.booking_line_id && ` · ${t("bookings.costLineAutoTag")}`}
    </span>
  );
}

function FinanceLineList({
  bookingRef,
  bookingId,
  kind,
  canWrite,
  showVoided,
  costLines,
}: {
  bookingRef: string;
  bookingId: string;
  kind: FinanceLineKind;
  canWrite: boolean;
  showVoided: boolean;
  costLines: CostLineOption[];
}) {
  const { t } = useTranslation();
  const path = LINE_PATH[kind];
  const [editingId, setEditingId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["finance-lines", bookingRef, kind, showVoided],
    queryFn: () =>
      api.get<Page<MoneyRow>>(
        `/finance/${path}?booking_id=${bookingId}&limit=100${
          showVoided ? "&include_deleted=true" : ""
        }`,
      ),
  });
  // A void / restore updates the line list and the profit rollup on this page.
  const action = useEntityAction([
    ["finance-lines", bookingRef],
    ["booking", bookingRef],
    ["booking-lines", bookingRef],
  ]);

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorNote message={(q.error as Error).message} />;
  const rows = q.data!.items;
  if (rows.length === 0)
    return <p className="mt-2 text-sm text-sand-muted">{t("bookings.noLines")}</p>;

  return (
    <ul className="mt-2 divide-y divide-olive-700 text-sm">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-wrap items-center gap-3 py-1.5">
          <span
            className={
              "font-medium" + (r.deleted_at ? " text-sand-muted line-through" : "")
            }
          >
            {formatMinor(r.amount_minor, r.currency)}
          </span>
          <StatusBadge status={r.status} />
          <span className="text-xs text-sand-muted">
            {formatCompanyDate(r.created_at)}
          </span>
          {kind === "cost" && costLines.length > 0 && (
            <CostLineTag row={r as CostRow} costLines={costLines} />
          )}
          {canWrite && (
            <span className="ml-auto flex items-center gap-2">
              {kind === "cost" && costLines.length > 0 && !r.deleted_at && editingId !== r.id && (
                <button
                  className="text-xs text-gold hover:underline"
                  onClick={() => setEditingId(r.id)}
                >
                  {t("bookings.changeCostLine")}
                </button>
              )}
              <RowActions
                deleted={!!r.deleted_at}
                canWrite={canWrite}
                busy={action.isPending}
                deleteLabel={t("finance.void")}
                onDelete={() =>
                  action.mutate(() => api.del(`/finance/${path}/${r.id}`))
                }
                onRestore={() =>
                  action.mutate(() =>
                    api.post(`/finance/${path}/${r.id}/restore`),
                  )
                }
              />
            </span>
          )}
          {kind === "cost" && editingId === r.id && (
            <div className="basis-full">
              <CostLineEditor
                costId={r.id}
                chosenLineId={(r as CostRow).booking_line_id ?? null}
                lines={costLines}
                invalidate={[
                  ["finance-lines", bookingRef],
                  ["booking", bookingRef],
                  ["booking-lines", bookingRef],
                ]}
                onDone={() => setEditingId(null)}
                onCancel={() => setEditingId(null)}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function BookingEditForm({
  booking,
  busy,
  error,
  onSave,
  onCancel,
}: {
  booking: Booking;
  busy: boolean;
  error: string | null;
  onSave: (patch: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canAgent = can("finance:write");
  const initialAgent = agentDraftFromBooking(booking.agent);
  const [agent, setAgent] = useState<AgentDraft>(initialAgent);
  const [f, setF] = useState({
    customer_name: booking.customer_name ?? "",
    customer_id: booking.customer_id,
    currency: booking.currency,
    start_date: booking.start_date ?? "",
    end_date: booking.end_date ?? "",
    notes: booking.notes ?? "",
  });
  const [tried, setTried] = useState(false);
  const customerMissing = !f.customer_id && f.customer_name.trim() === "";

  return (
    <Card>
      <div className="grid gap-3 sm:grid-cols-3">
        <Labeled
          label={t("fields.customerRecord")}
          required
          invalid={tried && customerMissing}
          error={t("bookings.customerRequired")}
        >
          <CustomerPicker
            value={f.customer_id}
            label={booking.customer_name}
            onChange={(id, label) =>
              setF((s) => ({
                ...s,
                customer_id: id,
                customer_name: label ?? s.customer_name,
              }))
            }
          />
        </Labeled>
        <Labeled label={t("bookings.customerNameManual")} required invalid={tried && customerMissing}>
          <TextField
            value={f.customer_name}
            onChange={(e) => setF({ ...f, customer_name: e.target.value })}
          />
        </Labeled>
        <Labeled label={t("fields.currency")}>
          <Select
            value={f.currency}
            onChange={(e) => setF({ ...f, currency: e.target.value })}
          >
            {CURRENCIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </Labeled>
        <Labeled label={t("fields.checkIn")}>
          <DateField
            value={f.start_date}
            onChange={(v) => setF({ ...f, start_date: v })}
          />
        </Labeled>
        <Labeled label={t("fields.checkOut")}>
          <DateField
            value={f.end_date}
            min={f.start_date || undefined}
            onChange={(v) => setF({ ...f, end_date: v })}
          />
        </Labeled>
        {canAgent && (
          <BookingAgentFields
            value={agent}
            onChange={(patch) => setAgent((prev) => ({ ...prev, ...patch }))}
            currency={f.currency}
            tried={tried}
          />
        )}
      </div>
      <div className="mt-3">
        <Labeled label={t("fields.notes")}>
          <TextArea
            value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
          />
        </Labeled>
      </div>
      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          loading={busy}
          onClick={() => {
            if (customerMissing || (canAgent && !agentDraftIsValid(agent))) {
              setTried(true); // what is required and blank glows; nothing is saved
              return;
            }
            const patch: Record<string, unknown> = {
              customer_name: f.customer_name || null,
              customer_id: f.customer_id,
              currency: f.currency,
              start_date: f.start_date || null,
              end_date: f.end_date || null,
              notes: f.notes || null,
            };
            // Only when «Откуда клиент» was touched: null = the client came directly.
            if (canAgent && agentDraftChanged(initialAgent, agent)) {
              patch.agent = agentDraftToPayload(agent);
            }
            onSave(patch);
          }}
        >
          {t("common.save")}
        </Button>
        <button
          className="text-sm text-sand-muted hover:text-sand"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </button>
      </div>
    </Card>
  );
}
